import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import ComponentNotifier from '../../../../services/wss/ComponentNotifier';
import GroupNodeCache from './GroupNodeCache';
import GroupNodeFetcher from './GroupNodeFetcher';
import OnEntryNodesResolvedHandler from '../YourNotesEventHandlers/OnEntryNodesResolvedHandler';
import OnEntriesAddedHandler from '../YourNotesEventHandlers/OnEntriesAddedHandler';
import OnNoteDeletedHandler from '../YourNotesEventHandlers/OnNoteDeletedHandler';
import OnGroupNodesLoadedHandler from '../YourNotesEventHandlers/OnGroupNodesLoadedHandler';
import { publish } from '../../../../services/utils/pubsub';
import { EngineEvents } from '../../../../services/wss/events/EngineEvents';
import { EngineNames } from '../../../../services/wss/events/EngineNames';
import OnSelectedEntryData from '../Events/OnSelectedEntryData';
import logService from '../../../../services/wss/logService3';
import groupNodeCache from './GroupNodeCache';

const _name = EngineNames.GroupNodeEngine;

class GroupNodeEngine extends EngineCleanupBase {
    _loader = new GroupNodeFetcher();

    _onEntryNodesResolved = new OnEntryNodesResolvedHandler();
    _onEntriesAdded = new OnEntriesAddedHandler();
    _onNoteDeleted = new OnNoteDeletedHandler();
    _onGroupNodesLoaded = new OnGroupNodesLoadedHandler();

    _activeGroupRk = null;
    _activeNode = null;
    _selectedEntry = null;

    constructor() {
        super();
    }

    // ─── lifecycle ────────────────────────────────────────

    bootup = () => {
        this.initialize(_name);
    };

    clear = () => {
        groupNodeCache.clear();
        this._activeGroupRk = null;
        this._activeNode = null;
        this._selectedEntry = null;
        super.clear();
    };

    // ─── public ───────────────────────────────────────────

    selectGroup = async (group) => {
        if (!group) return;

        // Skip if already active, e.g. on tab switch
        if (this._activeGroupRk === group.rk) return;

        this._activeGroupRk = group.rk;
        groupNodeCache.clear();
        // _selectedEntry not reset — user may want to undo the switch

        const data = await this._loader.fetchNodes(group.rk);
        if (!data) {
            logService.logError(`${_name}.selectGroup: loadGroup returned null for gid=${group.rk}`);
            return;
        }

        this.onGroupNodesLoaded(data);
    };

    // CHANGED — direction param: 'next' selects first entry, 'prev' selects last entry after load
    selectNode = async (nodeKey, direction = 'next') => {
        if (this._activeNode === nodeKey) return;
        this._activeNode = nodeKey;
        this._selectedEntry = null;
        if (!groupNodeCache.hasKey(nodeKey)) {
            await this._loadNode(nodeKey, 1, direction);
        } else {
            // ADDED — select from cache when already loaded
            const entries = groupNodeCache.getEntries(nodeKey);
            if (direction === 'next') this._selectedEntry = entries[0] ?? null;
            if (direction === 'prev') this._selectedEntry = entries[entries.length - 1] ?? null;
            this._notify();
        }
    };

    selectEntry = (entry) => {
        this._selectedEntry = entry;
        ComponentNotifier.notify(_name, 'entrySelected', entry);

        // fire engine state event so TopicGroupEngine can persist activeNodeId + activeEntryId
        const event = this._createActivityEvent(entry);
        publish(EngineEvents.channel, event);

        // notify painters aka hooks
        this._notify();
    };

    // direction param: 'next' selects first entry, 'prev' selects last entry after page load
    loadPage = async (nodeKey, page, direction = null) => await this._loadNode(nodeKey, page, direction);

    getNodes = () => groupNodeCache.getKeys().map((k) => groupNodeCache.getNode(k));

    getActiveNode = () => this._activeNode;
    getSelectedEntry = () => this._selectedEntry;
    getCurrentPage = (nodeKey) => groupNodeCache.getPage(nodeKey);

    // ─── called by PageContentSRHandlers ─────────────────

    onGroupNodesLoaded = (data) => {
        if (!this._onGroupNodesLoaded.handle(data)) {
            return;
        }

        this._setActive(data.gid, data.activeNodeId, data.activeEntryId, data.nodes);
        this._notify();
    };

    onEntryNodesResolved = (data) => {
        if (!this._onEntryNodesResolved.handle(data)) {
            return;
        }

        if (this._selectedEntry && data?.entry?.rk === this._selectedEntry.rk) {
            this._selectedEntry = data.entry;
        }

        this._notify();
    };

    onEntriesAdded = (data) => {
        const relevant = data.filter((cmd) => cmd.gid === this._activeGroupRk);
        if (!this._onEntriesAdded.handle(relevant)) return;
        this._notify();
    };

    /**
     * Handles note deletion — removes from cache and clears selection if active.
     * Called directly from PageContentSRHandlers.
     * @param {string} rk - row key of the deleted entry
     */
    onNoteDeleted = (rk) => {
        if (!this._onNoteDeleted.handle(rk)) return;
        // clear selection if the deleted entry was active
        if (this._selectedEntry?.rk === rk) this._selectedEntry = null;
        this._notify();
    };

    // ─── private ──────────────────────────────────────────

    _createActivityEvent = (entry) => {
        const event = {
            name: EngineEvents.onSelectedEntry,
            target: EngineNames.TopicGroupEngine,
            data: new OnSelectedEntryData(this._activeGroupRk, this._activeNode, entry?.rk ?? null),
        };

        return event;
    };

    // Find selected entry directly in the known node — no need to scan all nodes
    _findEntry = (nodeKey, rk) => {
        try {
            if (!nodeKey || !rk) return null;
            const entries = groupNodeCache.getEntries(nodeKey);
            return entries.find((e) => e.rk === rk) ?? null;
        } catch (error) {
            this.logError('_findEntry', error.message);
        }
    };

    _setActive = (gid, nodeId, entryId, nodes) => {
        try {
            this._activeGroupRk = gid;
            // Restore activeNodeId from group — fallback to first node with entries
            this._activeNode = nodeId ?? this._resolveFirstNode();
            // CHANGED — fallback to first entry if no active entry provided
            if (entryId) {
                this._selectedEntry = this._findEntry(this._activeNode, entryId);
            } else {
                const entries = groupNodeCache.getEntries(this._activeNode);
                this._selectedEntry = entries[0] ?? null;
            }

            // this.logError('_setActive', this._selectedEntry);
        } catch (error) {
            this.logError('_setActive', error.message);
        }
    };

    // direction param: 'next' selects first entry, 'prev' selects last entry after page load
    _loadNode = async (nodeKey, page, direction = null) => {
        try {
            const data = await this._loader.fetchPage(this._activeGroupRk, nodeKey, page);
            if (!data) return;
            // preserve key and label from existing cache entry
            const existing = groupNodeCache.getNode(nodeKey);
            groupNodeCache.setNode(nodeKey, {
                key: nodeKey,
                label: existing?.label ?? '',
                entries: data.entries,
                total: data.total,
                page: data.page,
            });
            // auto-select first or last entry based on navigation direction
            if (direction === 'next') this._selectedEntry = data.entries[0] ?? null;
            if (direction === 'prev') this._selectedEntry = data.entries[data.entries.length - 1] ?? null;
            this._notify();
        } catch (error) {
            this.logError('_loadNode', error.message);
        }
    };

    // Select first node that has entries, fallback to first key
    _resolveFirstNode = () => {
        const keys = groupNodeCache.getKeys();
        return keys.find((k) => groupNodeCache.getEntries(k).length > 0) ?? keys[0] ?? 'g1';
    };

    _notify = (event = 'nodesChanged') => {
        const data = {
            activeNode: this._activeNode,
            nodes: this.getNodes(),
            selectedEntry: this._selectedEntry,
        };

        ComponentNotifier.notify(_name, event, data);
    };
}

const groupNodeEngine = new GroupNodeEngine();
export default groupNodeEngine;
